<template>
  <div class="difficulty-slider-container">
    <label class="difficulty-label" :for="sliderId">
      <i class="fas fa-chart-line"></i>
      Difficulty Rating
      <i class="fas fa-info-circle difficulty-info" 
         :title="tooltipText"
         data-bs-toggle="tooltip" 
         data-bs-placement="top"></i>
    </label>
    
    <div class="slider-wrapper">
      <div class="slider-labels">
        <span class="label-start">1 - Very Easy</span>
        <span class="label-end">10 - Very Hard</span>
      </div>
      
      <input
        :id="sliderId"
        type="range"
        min="1"
        max="10"
        step="1"
        :value="modelValue || 5"
        @input="updateValue"
        class="difficulty-slider"
        :class="getDifficultyClass(modelValue || 5)"
      />
      
      <div class="slider-value">
        <span class="current-value" :class="getDifficultyClass(modelValue || 5)">
          {{ modelValue || 5 }}
        </span>
        <span class="difficulty-text">{{ getDifficultyText(modelValue || 5) }}</span>
      </div>
      
      <div class="slider-track-marks">
        <div v-for="mark in 10" :key="mark" class="track-mark" :class="{ active: mark <= (modelValue || 5) }"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { v4 as uuidv4 } from 'uuid'

const props = defineProps({
  modelValue: {
    type: Number,
    default: 5
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

// ID unique pour le slider
const sliderId = `difficulty-slider-${uuidv4().slice(0, 8)}`

// Texte de l'infobulle
const tooltipText = computed(() => 
  'Estimate the difficulty of this item from 1 (very easy) to 10 (very hard). ' +
  'This helps the CAT system adapt the test to the learner\'s level. ' +
  'Default value is 5 (medium difficulty).'
)

// Mise à jour de la valeur
const updateValue = (event) => {
  const value = parseInt(event.target.value)
  emit('update:modelValue', value)
}

// Obtenir la classe CSS selon la difficulté
const getDifficultyClass = (value) => {
  if (value <= 2) return 'very-easy'
  if (value <= 4) return 'easy'
  if (value <= 6) return 'medium'
  if (value <= 8) return 'hard'
  return 'very-hard'
}

// Obtenir le texte de difficulté
const getDifficultyText = (value) => {
  const texts = {
    1: 'Very Easy', 2: 'Very Easy',
    3: 'Easy', 4: 'Easy', 
    5: 'Medium', 6: 'Medium',
    7: 'Hard', 8: 'Hard',
    9: 'Very Hard', 10: 'Very Hard'
  }
  return texts[value] || 'Medium'
}

// Initialisation des tooltips Bootstrap
onMounted(() => {
  // Initialiser les tooltips Bootstrap si disponible
  if (window.bootstrap && window.bootstrap.Tooltip) {
    const tooltipElement = document.querySelector(`[for="${sliderId}"] .difficulty-info`)
    if (tooltipElement) {
      new window.bootstrap.Tooltip(tooltipElement)
    }
  }
})
</script>

<style scoped>
.difficulty-slider-container {
  margin: 15px 10px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.difficulty-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
  font-size: 0.9em;
}

.difficulty-info {
  color: #007bff;
  cursor: help;
  font-size: 0.8em;
}

.slider-wrapper {
  position: relative;
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 0.75em;
  color: #6c757d;
}

.difficulty-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e9ecef;
  outline: none;
  -webkit-appearance: none;
  margin: 10px 0;
}

.difficulty-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  transition: all 0.2s ease;
}

.difficulty-slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 3px 6px rgba(0,0,0,0.3);
}

.difficulty-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

/* Couleurs selon la difficulté */
.difficulty-slider.very-easy::-webkit-slider-thumb {
  background: #28a745;
}

.difficulty-slider.easy::-webkit-slider-thumb {
  background: #20c997;
}

.difficulty-slider.medium::-webkit-slider-thumb {
  background: #ffc107;
}

.difficulty-slider.hard::-webkit-slider-thumb {
  background: #fd7e14;
}

.difficulty-slider.very-hard::-webkit-slider-thumb {
  background: #dc3545;
}

.slider-value {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 8px;
}

.current-value {
  font-size: 1.2em;
  font-weight: bold;
  min-width: 25px;
  text-align: center;
  padding: 4px 8px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #dee2e6;
}

.current-value.very-easy { color: #28a745; border-color: #28a745; }
.current-value.easy { color: #20c997; border-color: #20c997; }
.current-value.medium { color: #ffc107; border-color: #ffc107; }
.current-value.hard { color: #fd7e14; border-color: #fd7e14; }
.current-value.very-hard { color: #dc3545; border-color: #dc3545; }

.difficulty-text {
  font-size: 0.9em;
  color: #6c757d;
  font-weight: 500;
}

.slider-track-marks {
  display: flex;
  justify-content: space-between;
  margin-top: -15px;
  padding: 0 10px;
}

.track-mark {
  width: 2px;
  height: 8px;
  background: #dee2e6;
  border-radius: 1px;
  transition: background-color 0.2s ease;
}

.track-mark.active {
  background: #007bff;
}

/* Responsive */
@media (max-width: 768px) {
  .difficulty-slider-container {
    padding: 12px;
  }
  
  .slider-labels {
    font-size: 0.7em;
  }
  
  .current-value {
    font-size: 1em;
  }
}
</style>